import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, useTheme,  IconButton } from '@mui/material';
import { itemPercentage, calcProPercentsBasedOnFilterAndDays } from '../../../utilis/common';
import { calculateDiffBetweenPercentages, pickComparisonColor } from '../../../utilis/percentage-comparison';
import InfoIcon from '../../../components/icon/InfoIcon';
import HtmlTooltip from '../../../components/shared/HtmlTooltip';
import ReportButton from '../../../components/shared/ReportButton';
import { OVERALL_PAGE_SUB_TYPE, PAGE_TYPE } from '../../../types/pages.type';
import CommonIconButtons from '../../../components/dashboard/card/RightAction';
import CardTooltipContent from '../../../components/dashboard/card/CardTooltipContent';
import ColorBox from '../../../components/shared/dashboard/ColorBox';

const MobileOverallCarousel = ({
	loading,
	selected,
	handleSelectCard,
	dbData,
	dbDataComparison,
	filter,
	filterComparison,
	percentageAgainst,
	percentageBy,
	projectionDays,
	handleClickCardTransferType,
	handleOnClickAction,
	handleOnClickReport,
	tooltipsInfo,
	isLayoutMode = false,
	getTotalCount,
	filterTotal,
	isCompareColor = false
}) => {
	const theme = useTheme();
	const [currentIndex, setCurrentIndex] = useState(0);

	// Animation state
	const [isAnimating, setIsAnimating] = useState(false);
	const timerRef = useRef(null);

	// Touch/swipe state
	const [touchStart, setTouchStart] = useState(null);
	const [touchEnd, setTouchEnd] = useState(null);
	const [isDragging, setIsDragging] = useState(false);

	// Minimum swipe distance (in px)
	const minSwipeDistance = 50;

	// Touch handlers
	const onTouchStart = (e) => {
		setTouchEnd(null);
		setTouchStart(e.targetTouches[0].clientX);
		setIsDragging(true);
	};

	const onTouchMove = (e) => {
		if (touchStart !== null) {
			setTouchEnd(e.targetTouches[0].clientX);
		}
	};

	const onTouchEnd = () => {
		if (!touchStart || !touchEnd) {
			setIsDragging(false);
			setTouchStart(null);
			setTouchEnd(null);
			return;
		}

		const distance = touchStart - touchEnd;
		const isLeftSwipe = distance > minSwipeDistance;
		const isRightSwipe = distance < -minSwipeDistance;

		if (isLeftSwipe) {
			// Swipe left - go to next card
			const nextIndex = (currentIndex + 1) % cardData.length;
			setCurrentIndex(nextIndex);
			// Auto-select the card
			const nextCard = cardData[nextIndex];
			if (!selected.includes(nextCard.type)) {
				handleAnimationChange();
				handleClickCardTransferType(true, nextCard.type);
			}
			// Haptic feedback for mobile devices
			if (navigator.vibrate) {
				navigator.vibrate(50);
			}
		} else if (isRightSwipe) {
			// Swipe right - go to previous card
			const prevIndex = (currentIndex - 1 + cardData.length) % cardData.length;
			setCurrentIndex(prevIndex);
			// Auto-select the card
			const prevCard = cardData[prevIndex];
			if (!selected.includes(prevCard.type)) {
				handleAnimationChange();
				handleClickCardTransferType(true, prevCard.type);
			}
			// Haptic feedback for mobile devices
			if (navigator.vibrate) {
				navigator.vibrate(50);
			}
		}

		setIsDragging(false);
		setTouchStart(null);
		setTouchEnd(null);
	};

	// Animation handler
	const handleAnimationChange = useCallback(async () => {
		setIsAnimating(true);
		await new Promise(resolve => setTimeout(resolve, 0)); // Ensure state update

		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		const stopAnimation = () => {
			setIsAnimating(false);
			timerRef.current = null;
		};

		if (loading) {
			const checkLoading = () => {
				if (!loading) {
					stopAnimation();
				} else {
					requestAnimationFrame(checkLoading); // Continue checking
				}
			};
			requestAnimationFrame(checkLoading); // Start checking
		} else {
			timerRef.current = setTimeout(stopAnimation, 500); // Minimum 500ms
		}
	}, [loading]);

	useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, []);

	const cardData = [
		{
			type: OVERALL_PAGE_SUB_TYPE.TOTAL,
			title: "Total +/-",
			total: getTotalCount(dbData, false),
			totalComparison: getTotalCount(dbDataComparison, false),
			color: theme.palette.primary.main,
			key: "total",
			subTitleText: "30 Day Transfer Rate"
		},
		{
			type: OVERALL_PAGE_SUB_TYPE.TOTAL_OUTGOING,
			title: "Total Outgoing",
			total: dbData?.totalOutgoing || 0,
			totalComparison: dbDataComparison?.totalOutgoing || 0,
			color: theme.palette.secondary.main,
			key: "totalOutgoing",
			subTitleText: percentageBy
		},
		{
			type: OVERALL_PAGE_SUB_TYPE.TOTAL_INCOMING,
			title: "Total Incoming",
			total: dbData?.totalIncoming || 0,
			totalComparison: dbDataComparison?.totalIncoming || 0,
			color: theme.palette.error.main,
			key: "totalIncoming",
			subTitleText: percentageBy
		}
	];

	const renderCard = (card, index) => {
		const isSelected = selected.includes(card.type);

		// Calculate values using the same logic as desktop TotalOverallCard
		const totalWithOutSign = card.total;
		const totalComparison = card.totalComparison;

		// Calculate percentage using the same logic as desktop
		const percentage = itemPercentage(totalWithOutSign, percentageAgainst, "number", PAGE_TYPE.OVERALL, true);
		const percentageComparison = itemPercentage(totalComparison, dbDataComparison?.censusAverage, "number", PAGE_TYPE.OVERALL, true);

		// Calculate projection values
		// const totalProjection = projectionPerMonth(card.total, filter, projectionDays);
		// const totalProjectionPercentage = itemPercentage(totalProjection, percentageAgainst, "number");

		// Calculate comparison differences using the same logic as desktop
		const { percentageDiff, itemTotalComparisonScaled, numberOfDays } = calculateDiffBetweenPercentages(
			percentage,
			percentageComparison,
			true,
			filter,
			filterComparison,
			totalComparison,
			projectionDays
		);

		const comparisonDiffColor = pickComparisonColor(percentageDiff, true);

		// Calculate display values using calcProPercentsBasedOnFilterAndDays
		const displayTotal = calcProPercentsBasedOnFilterAndDays(card.total, filter, projectionDays);
		const displayPercentage = calcProPercentsBasedOnFilterAndDays(percentage, filter, projectionDays, filterTotal, percentageAgainst);

		// Calculate position and scale based on index
		const isActive = index === currentIndex;
		const isLeft = index === (currentIndex - 1 + cardData.length) % cardData.length;
		const isRight = index === (currentIndex + 1) % cardData.length;

		let transform = '';
		let scale = 1;
		let opacity = 1;
		let zIndex = 1;

		if (isActive) {
			transform = 'translateX(0)';
			scale = 1;
			opacity = 1;
			zIndex = 3;
		} else if (isLeft) {
			transform = 'translateX(-60%)';
			scale = 0.8;
			opacity = 0.7;
			zIndex = 2;
		} else if (isRight) {
			transform = 'translateX(60%)';
			scale = 0.8;
			opacity = 0.7;
			zIndex = 2;
		} else {
			transform = 'translateX(0)';
			scale = 0.6;
			opacity = 0.3;
			zIndex = 1;
		}

		return (
			<Box
				key={card.type}
				sx={{
					position: 'absolute',
					left: '50%',
					top: '50%',
					transform: `translate(-50%, -50%) ${transform} scale(${scale})`,
					opacity,
					zIndex,
					transition: isDragging ? 'none' : 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
					width: '80%',
					maxWidth: '280px',
					pointerEvents: isActive ? 'auto' : 'none',
					touchAction: 'none', // Disable default touch actions on cards
				}}
			>
				<Box
					sx={{
						border: `${isSelected ? '3px' : '2px'} solid ${isSelected ? '#4879f5' : 'transparent'}`,
						borderRadius: 2,
						backgroundColor: 'white',
						boxShadow: isActive ? 3 : 1,
						transition: 'all 0.2s ease',
						cursor: isActive && !isLayoutMode ? 'pointer' : 'default',
						...(isAnimating && {
							animation: 'pulseGlow 1.5s forwards',
							'@keyframes pulseGlow': {
								'0%': {
									opacity: 0.7,
									boxShadow: '0 0 10px #4cebeb',
								},
								'100%': {
									opacity: 1,
									boxShadow: '0 0 20px #076673',
								},
							},
						}),
						'&:hover': isSelected ? {
							borderColor: '#4879f5',
							boxShadow: 4,
						} : {},
					}}
					onClick={() => {
						if (isActive && !loading && !isLayoutMode) {
							handleAnimationChange();
							handleClickCardTransferType(!isSelected, card.type);
						}
					}}
				>
					{/* Card Header - Matching Desktop Functionality */}
					<Box sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						px: 2,
						py: 1,
						p: 2,
						pt: 2.5,
						borderBottom: '1px solid #f6f6f9',
						color: '#444652'
					}}>
						{/* Title */}
						<Box sx={{ display: 'flex', alignItems: 'center', flex: 1, maxWidth: '44%' }}>
							<Typography
								variant="subtitle1"
								component="p"
								sx={{
									fontSize: '0.8rem',
									fontWeight: 500,
									color: '#444652',
									overflow: 'hidden',
									whiteSpace: 'nowrap',
									textOverflow: 'ellipsis',
								}}
							>
								{card.title}
							</Typography>
						</Box>

						{/* Right Side Actions */}
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
							{/* Info Tooltip */}
							<Box>
								{tooltipsInfo?.overallPage?.[card.key.toLowerCase()] && (
									<HtmlTooltip content={<CardTooltipContent content={tooltipsInfo.overallPage[card.key.toLowerCase()]} />}>
										<IconButton
											disableFocusRipple={true}
											disableRipple={true}
											sx={{
												p: 0.5,
												width: 20,
												height: 20,
												color: 'black',
												'&:hover': {
													color: '#4879f5'
												}
											}}
										>
											<InfoIcon />
										</IconButton>
									</HtmlTooltip>
								)}
							</Box>

							{/* Report Button */}
							{loading ? (
								<Box
									sx={{
										width: 20,
										height: 20,
										borderRadius: "50%",
										marginRight: "7px",
										backgroundColor: 'grey.300'
									}}
								/>
							) : (
								<ReportButton
									handleOnClickReport={(title, otherData) => handleOnClickReport(card.key, title, true, otherData)}
									loading={loading}
									title={card.title}
									openFrom="card"
									pageType={PAGE_TYPE.OVERALL}
									isCustomAlertEnable={false}
									isMobile={true}
								/>
							)}

							{/* Action Buttons */}
							<CommonIconButtons
								title={card.title}
								handleOnClickAction={(title, defaultTab) => handleOnClickAction(title, defaultTab)}
								loading={loading}
								isMobile={true}
							/>
						</Box>
					</Box>

					{/* Card Content - Professional Layout */}
					<Box sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						p: 3,
						pt: 2.5,
						pb: 3,
						height: '120px'
					}}>
						{/* Left Section - Main Total (50% width) */}
						<Box sx={{
							width: '50%',
							borderRight: card.type !== OVERALL_PAGE_SUB_TYPE.TOTAL ? '1px solid #f1f1f4' : 'none',
							color: '#444652',
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'center',
							height: '100%',
							pr: 2
						}}>
							{/* Title */}
							<Typography
								textAlign="center"
								variant="subtitle2"
								sx={{
									fontSize: '12px',
									fontWeight: 400,
									fontFamily: '"manrope-reg"',
									color: '#444652',
									mb: 0.8,
									letterSpacing: '0.5px',
									mt: "-5px"
								}}
							>
								{card.title}
							</Typography>

							{/* Main Total with Checkbox */}
							<Box sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								gap: 1.5
							}}>
								{/* Checkbox */}
								{/* <Checkbox
									checked={isSelected}
									disabled={isLayoutMode}
									onChange={(e) => {
										e.preventDefault();
										e.stopPropagation();
										if (!isLayoutMode) {
											handleClickCardTransferType(e.target.checked, card.type);
										}
									}}
									sx={{
										padding: 0,
										'& .MuiSvgIcon-root': {
											fontSize: '1.1rem',
										},
										color: '#4879f5',
										'&.Mui-checked': {
											color: '#4879f5',
										},
									}}
								/> */}

								{/* Total Number with ColorBox - Matching Desktop Logic */}
								<Box sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 1,
									flexDirection: 'row'
								}}>
									<Typography
										sx={{
											fontWeight: 700,
											fontSize: '1.8rem',
											fontFamily: '"manrope-bold"',
											lineHeight: 1,
											background: 'linear-gradient(180deg, #FF0000 0%, #F5D948 100%)',
											WebkitBackgroundClip: 'text',
											WebkitTextFillColor: 'transparent',
											backgroundClip: 'text',
											textAlign: 'center',
											display: 'inline-block'
										}}
									>
										{loading ? '...' : card?.type === OVERALL_PAGE_SUB_TYPE.TOTAL ? (displayTotal < 0 ? "" : "+") + displayTotal : displayTotal}
									</Typography>
									{comparisonDiffColor && isCompareColor && !loading && (
										<ColorBox
											color={comparisonDiffColor}
											comparingAgainst={totalComparison}
											comparingAgainstScaled={itemTotalComparisonScaled}
											numberOfDays={numberOfDays}
											loading={loading}
											loadingComparison={false}
											isLayoutMode={false}
											sx={{
												width: "8px",
												height: "16px",
												position: "relative",
												left: "0px",
												display: 'inline-block'
											}}
										/>
									)}
								</Box>
							</Box>
						</Box>
						{/* Right Section - Percentage (50% width) */}
						{card.type !== OVERALL_PAGE_SUB_TYPE.TOTAL && (
							<Box sx={{
								pl: 2,
								pt: 2.2,
								width: '50%',
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'center',
								alignItems: 'center',
								height: '100%'
							}}>
								{/* Secondary Title */}
								<Typography
									textAlign="center"
									variant="subtitle2"
									sx={{
										fontSize: '12px',
										fontWeight: 400,
										fontFamily: '"manrope-reg"',
										color: '#444652',
										mt: "2px",
										letterSpacing: '0.5px'
									}}
								>
									{card.subTitleText}
								</Typography>

								{/* Percentage Display */}
								<Box sx={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									flexDirection: 'column',
									gap: 1.5
								}}>
									{/* Percentage - Matching Desktop Logic */}
									<Typography
										sx={{
											fontWeight: 700,
											fontSize: '1.8rem',
											fontFamily: '"manrope-bold"',
											lineHeight: 1,
											background: 'linear-gradient(125.15deg, #eab80e -7.95%, #ffd399 96.28%)',
											WebkitBackgroundClip: 'text',
											WebkitTextFillColor: 'transparent',
											backgroundClip: 'text',
											textAlign: 'center',
											display: 'inline-block',
											mt: 1
										}}
									>
										{loading ? '...' : `${displayPercentage}%`}
									</Typography>

									{/* Icon placeholder */}
									<Box sx={{ width: '20px', height: '16px' }}>
										{/* You can add an icon here if needed */}
									</Box>
								</Box>
							</Box>
						)}
					</Box>
				</Box>
			</Box>
		);
	};

	return (
		<Box sx={{ p: 2, pt: 0, pb: 0, mt: -3 }}>
			{/* Carousel Container */}
			<Box
				onTouchStart={onTouchStart}
				onTouchMove={onTouchMove}
				onTouchEnd={onTouchEnd}
				sx={{
					position: 'relative',
					height: '240px',
					width: '100%',
					overflow: 'hidden',
					borderRadius: 2,
					touchAction: 'pan-x', // Allow horizontal swipes, prevent vertical scrolling
					userSelect: 'none', // Prevent text selection during swipe
				}}
			>
				{/* Cards */}
				{cardData.map((card, index) => renderCard(card, index))}

				{/* Dots Indicator */}
				<Box sx={{
					position: 'absolute',
					bottom: 16,
					left: '50%',
					transform: 'translateX(-50%)',
					display: 'flex',
					gap: 1,
					zIndex: 4,
				}}>
					{cardData.map((_, index) => (
						<Box
							key={index}
							sx={{
								width: 8,
								height: 8,
								borderRadius: '50%',
								backgroundColor: index === currentIndex ? 'primary.main' : 'rgba(0, 0, 0, 0.2)',
								cursor: 'pointer',
								transition: 'all 0.2s ease',
								'&:hover': {
									backgroundColor: index === currentIndex ? 'primary.main' : 'rgba(0, 0, 0, 0.4)',
								},
							}}
							onClick={() => {
								setCurrentIndex(index);
								// Auto-select the card when clicking on dots
								const clickedCard = cardData[index];
								if (!selected.includes(clickedCard.type)) {
									handleAnimationChange();
									handleClickCardTransferType(true, clickedCard.type);
								}
							}}
						/>
					))}
				</Box>
			</Box>
		</Box>
	);
};

export default MobileOverallCarousel; 